import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, finalize, takeUntil } from 'rxjs';
import { AppModeService } from 'src/app/services/app-mode.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-change-password-page',
  templateUrl: './change-password-page.component.html',
  styleUrls: ['./change-password-page.component.css'],
})
export class ChangePasswordPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly hasForgotPasswordRoute = false;

  changePasswordForm: FormGroup;
  showCurrent = false;
  showNew = false;
  showConfirm = false;
  isSaving = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    public readonly appModeService: AppModeService,
    private readonly authService: AuthService,
  ) {
    this.changePasswordForm = this.fb.group(
      {
        currentPassword: ['', [Validators.required]],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/),
          ],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator() },
    );
  }

  ngOnInit(): void {
    const lang = localStorage.getItem('lang') || 'en';
    if (!this.appModeService.isApp()) {
      this.router.navigateByUrl(`/${lang}/home`);
      return;
    }

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (!user) {
          this.router.navigateByUrl(`/${lang}/home`);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentPasswordControl(): AbstractControl | null {
    return this.changePasswordForm.get('currentPassword');
  }

  get newPasswordControl(): AbstractControl | null {
    return this.changePasswordForm.get('newPassword');
  }

  get confirmPasswordControl(): AbstractControl | null {
    return this.changePasswordForm.get('confirmPassword');
  }

  goBack(): void {
    const lang = localStorage.getItem('lang') || 'en';
    this.router.navigateByUrl(`/${lang}/profile`);
  }

  onForgotPassword(): void {
    if (!this.hasForgotPasswordRoute) {
      return;
    }
    const lang = localStorage.getItem('lang') || 'en';
    this.router.navigateByUrl(`/${lang}/forgot-password`);
  }

  onSubmit(): void {
    this.successMessage = null;
    this.errorMessage = null;

    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const currentPassword = this.currentPasswordControl?.value;
    const newPassword = this.newPasswordControl?.value;

    this.authService
      .changePassword(currentPassword, newPassword)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.successMessage = 'PROFILE.SUCCESS_PASSWORD_CHANGED';
          this.changePasswordForm.reset();
          this.errorMessage = null;
        },
        error: (error) => {
          if (error?.status === 400) {
            this.errorMessage = 'PROFILE.ERROR_WRONG_CURRENT_PASSWORD';
            return;
          }
          this.errorMessage = 'PROFILE.ERROR_CHANGE_PASSWORD_FAILED';
        },
      });
  }
  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const newPassword = control.get('newPassword')?.value;
      const confirmPassword = control.get('confirmPassword')?.value;
      if (!confirmPassword) {
        return null;
      }
      return newPassword === confirmPassword
        ? null
        : { passwordMismatch: true };
    };
  }
}
